import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button as PrimeButton } from 'primereact/button';

import { actionCreators } from '../../store/LoginActionsReducers';
import api from '../../ApiService';
import { CallType, UserRoles, CallLogDepartment, IsMobile } from '../../helper';
import { EditorHeader } from '../EditorHeader';
import { ModalOKCancel } from '../../modals';

class CallLog extends React.Component {
    constructor(props) {
        super(props);

        let isReadOnly = !(props.userRole === UserRoles.Administrator || props.userRole === UserRoles.SuperUser
            || props.userRole === UserRoles.StaffUser || props.userRole === UserRoles.StaffAnalyticsManager
            || props.userRole === UserRoles.StaffManagement);

        this.state = {
            isDirty: false,
            isEditorLoading: true,
            isTableLoading: true,
            editModel: null,
            callLogEntries: [],
            contacts: [],
            selectedHospital: props.selectedHospital,
            isReadOnly: isReadOnly,
            selectedRow: null,
            showModal: false,
            modalTitle: null,
            modalText: null,
            modalAction: null
        };

        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        this.onCallHistorySelect = this.onCallHistorySelect.bind(this);
    }

    componentDidMount = async () => {
        this.props.alertCallLogComponentOfHospitalChange(this.alertCallLogComponentOfHospitalChange);
        await this.loadPage();
    }

    alertCallLogComponentOfHospitalChange = (hospital) => {
        this.setState({ selectedHospital: hospital }, this.loadPage);
    }

    loadPage = async () => {
        this.setState({
            isDirty: false,
            isEditorLoading: true,
            isTableLoading: true,
            editModel: null
        });

        // Get calls
        let callLogEntries = await api.getCallLog(this.state.selectedHospital.nameHash);
        let contacts = await api.getContacts(this.state.selectedHospital.nameHash);
        let editModel = this.getNewCallLogForEdit();

        // Filtering out callLog entries for 'users'
        if (this.state.isReadOnly) callLogEntries = callLogEntries.filter(x => x.externalNotes !== '');

        this.setState({
            isEditorLoading: false,
            isTableLoading: false,
            callLogEntries,
            contacts,
            editModel
        });
    }

    formatDateForEditor = (date) => {
        // Reformat date to play nice with date editor.
        let editorDate = new Date(date);
        let dd = String(editorDate.getDate()).padStart(2, '0');
        let mm = String(editorDate.getMonth() + 1).padStart(2, '0'); //January is 0!
        let yyyy = editorDate.getFullYear();

        return `${yyyy}-${mm}-${dd}`;
    }

    getNewCallLogForEdit = () => {
        return {
            id: 0,
            idiVetUser: this.state.selectedHospital.idiVetUser,
            contacts: [],
            callDate: this.formatDateForEditor(new Date()),
            callDurationMinutes: '',
            callType: CallType.Monthly,
            isCallWithOwner: false,
            externalNotes: '',
            internalNotes: '',
            department: CallLogDepartment.NotSelected
        };
    }

    handleSelectChange = event => {
        this.setState({
            isDirty: true,
            editModel: {
                ...this.state.editModel,
                [event.target.name]: event.target.value
            }
        });
    }

    handleCheckboxChange = (event, fieldName) => {
        this.setState({ editModel: { ...this.state.editModel, [fieldName]: event.target.checked }, isDirty: true });
    }

    handleContactChange = event => {
        let contacts = [];
        event.target.value.forEach((contactId) => {
            contacts.push({ callLogEntryId: this.state.editModel.id, contactId: contactId });
        });

        this.setState({
            isDirty: true,
            editModel: {
                ...this.state.editModel,
                contacts
            }
        });
    }

    handleTextBlur = event => {
        this.setState({
            isDirty: true,
            editModel: {
                ...this.state.editModel,
                [event.target.attributes.id.value]: event.target.value
            }
        }, this.refreshOptions);
    }

    handleTextChange = event => {
        if (!this.state.isDirty) {
            this.setState({ isDirty: true });
        }
    }

    onClickCancel = () => {
        // If not dirty, clear.
        if (!this.state.isDirty) {
            this.createNewCallLog();
            return;
        }

        // Dirty. Show alert. Are you sure?
        this.setState({
            showModal: true,
            modalTitle: "Discard changes",
            modalText: "You have unsaved changes. Click OK to discard your changes.",
            modalAction: this.createNewCallLog
        });
    }

    createNewCallLog = () => {
        this.setState({
            isDirty: false,
            editModel: this.getNewCallLogForEdit()
        });
    }

    updateCallEditor = (callLogEntryId) => {
        // Clone from state to edit model, otherwise editModel behaves as a reference & alters state in unexpected ways.
        let callLogEntry = this.state.callLogEntries.find((value) => value.id === callLogEntryId);

        // If call exists, load entry, otherwise leave unchanged.
        if (callLogEntry) {
            let editModel = {};
            Object.assign(editModel, callLogEntry); // Clone.
            editModel.callDate = this.formatDateForEditor(editModel.callDate); // Modify clone date.
            this.setState({ isDirty: false, editModel });
        }
        else {
            toast.error("Could not load call into editor.");
        }
    }

    onCallHistorySelect = (rowData) => {
        let updateAction = () => this.updateCallEditor(rowData.data.id);

        // Not dirty? Load right away & return.
        if (!this.state.isDirty) {
            updateAction();
            return;
        }

        // Editor has changes. Are you sure?
        this.setState({
            showModal: true,
            modalTitle: "Discard changes",
            modalText: "You have unsaved edits. Click OK to discard your changes and continue.",
            modalAction: updateAction
        });
    }

    onClickSave = async () => {
        if (this.state.editModel.id === 0) {
            // New record. Save w/ no modal.
            await this.saveCallLog();
            return;
        }

        // This is an edit.  Is user sure?
        this.setState({
            showModal: true,
            modalTitle: "Save edits",
            modalText: "You have edited an existing call record.  Click OK to save changes.",
            modalAction: this.saveCallLog
        });
    }

    onClickDelete = async (e, rowData) => {
        this.setState({
            selectedRow: rowData,
            showModal: true,
            modalTitle: "Delete a Call Entry",
            modalText: "You are deleting an existing call record.  Click OK to delete.",
            modalAction: this.deleteCallLog
        });
    }

    deleteCallLog = async () => {
        if (await api.deleteCallLog(this.props.selectedHospital.nameHash, this.state.selectedRow.id)) this.loadPage();
    }

    saveCallLog = async () => {
        let editModel = this.state.editModel;

        if (!editModel.callDurationMinutes) {
            toast.error("Call Duration needs to be selected.");
            return;
        }
        if (editModel.department === CallLogDepartment.NotSelected) {
            toast.error("Department needs to be selected.");
            return;
        }

        let saveResult = await api.saveCallLog(this.state.editModel, this.props.selectedHospital.nameHash);
        if (saveResult) {
            // Success. Re-load list & clear editor.
            await this.loadPage();
        }
    }

    renderSaveCancel = () => {
        return (
            <Grid item xs={12} sm={6} md={6} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }} >
                <Button variant='contained' color='default' style={{ marginRight: '15px', minWidth: '80px' }} onClick={this.onClickCancel} disabled={!this.state.isDirty && this.state.editModel.id === 0}>Cancel</Button>
                <Button variant='contained' color='primary' onClick={this.onClickSave} style={{ minWidth: '80px' }} disabled={!this.state.isDirty}>Save</Button>
            </Grid>
        );
    }

    getCallTypeText = (callType) => {
        if (callType === CallType.Monthly)
            return "Monthly";
        else if (callType === CallType.OneOff)
            return "One-off";
        else if (callType === CallType.Onboarding)
            return "Onboarding";
        else
            return "Unknown";
    }

    getDepartmentText = (department) => {
        if (department === CallLogDepartment.Marketing)
            return "Marketing";
        else if (department === CallLogDepartment.Analytics)
            return "Analytics";
        else if (department === CallLogDepartment.HRT)
            return "HR&T";
        else if (department === CallLogDepartment.Other)
            return "Other";
        else if (department === CallLogDepartment.POA)
            return "POA";
        else
            return "Unknown";
    }

    getSelectedRow = () => {
        let callLogEntry = this.state.callLogEntries.find((value) => value.id === this.state.editModel.id);
        return callLogEntry ? callLogEntry : undefined;
    }

    // Read-only viewer.  If we can refactor later, we might only want to maintain single shared form between read & edit w/
    // overridden disabled styling.
    renderCallViewer = () => {
        if (!this.state.editModel || !this.state.editModel.id)
            return null;

        return (
            <Grid container spacing={24}>
                {/* Contacts */}
                <Grid item xs={3}>
                    <InputLabel shrink>Contacts</InputLabel>
                    <div className="field-read-only">{this.renderContactNames(this.state.editModel)}</div>
                </Grid>

                {/* Call Date*/}
                <Grid item xs={3}>
                    <InputLabel shrink>Date</InputLabel>
                    <div className="field-read-only">{this.renderCallDate(this.state.editModel)}</div>
                </Grid>

                <Grid item xs={6} />

                {/* Notes */}
                <Grid item xs={12}>
                    <InputLabel shrink>Notes</InputLabel>
                    <TextField id='externalNotes' multiline
                        value={this.state.editModel.externalNotes}
                        inputProps={{ readOnly: true }}
                        fullWidth
                    />
                </Grid>

            </Grid>
        );
    }

    renderCallEditor = () => {
        if (this.state.isEditorLoading)
            return <em>Loading...</em>;

        let isReadOnly = this.state.isReadOnly;
        let shrinkOverride = isReadOnly ? true : undefined;

        let callDurationOptions = ["15", "30", "45", "60", "90", "120"];
        let callTypeOptions = [
            CallType.Monthly,
            CallType.OneOff,
            CallType.Onboarding
        ];
        let departments = [
            CallLogDepartment.Marketing,
            CallLogDepartment.Analytics,
            CallLogDepartment.HRT,
            CallLogDepartment.Other,
            CallLogDepartment.POA,
        ];

        let headerText = "View Call";
        if (!isReadOnly) {
            headerText = (this.state.editModel.id === 0 ? "Log New" : "View/Edit") + " Call";
        }

        let selectedContactIds = this.state.editModel.contacts.map(contact => contact.contactId);
        let selectedContactNames = '';

        let isFirst = true;
        selectedContactIds.forEach((contactId) => {
            let contact = this.state.contacts.find((value) => value.id === contactId);
            if (contact) {
                if (!isFirst) selectedContactNames += ', ';
                selectedContactNames += `${contact.firstName} ${contact.lastName}`;
            }
            isFirst = false;
        });

        const { editModel } = this.state;
        let notesInfoText = () => editModel.modifiedByAppUserName ? `Created By ${editModel.createdByAppUserName} on ${editModel.createdDate}, ` +
                                `Modified By ${editModel.modifiedByAppUserName} on ${editModel.modifiedDate}`
                                : `Created By ${editModel.createdByAppUserName} on ${editModel.createdDate}`;


        return (
            <div>
                <Grid container spacing={24}>
                    <Grid item xs={12} sm={6} md={6}>
                        <EditorHeader text={headerText} isReadOnly={this.state.isReadOnly} isNew={this.state.editModel.id === 0} />
                    </Grid>
                    {this.renderSaveCancel()}

                    { /* Contacts - dropdown multi-select*/}
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth disabled={isReadOnly}>
                            <InputLabel htmlFor="contacts">Contacts *</InputLabel>
                            <Select
                                value={selectedContactIds}
                                onChange={this.handleContactChange}
                                multiple
                                input={<Input />}
                                renderValue={() => selectedContactNames}
                            >
                                {this.state.contacts.map(contact => (
                                    <MenuItem key={contact.id} value={contact.id}>
                                        <Checkbox checked={selectedContactIds.indexOf(contact.id) > -1} />
                                        <ListItemText primary={`${contact.firstName} ${contact.lastName}`} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    { /* Call Date - text + date picker */}
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField id='callDate' type='date' InputLabelProps={{ shrink: true }} label='Date Called *'
                            defaultValue={this.state.editModel.callDate || ''} key={this.state.editModel.callDate || ''}
                            onBlur={this.handleTextBlur} onChange={this.handleTextChange} disabled={isReadOnly} fullWidth
                        />
                    </Grid>

                    { /* Call Type - dropdown - Always shrink label */}
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth disabled={isReadOnly}>
                            <InputLabel htmlFor='callType' shrink>Type *</InputLabel>
                            <Select
                                value={this.state.editModel.callType}
                                onChange={this.handleSelectChange}
                                inputProps={{ name: 'callType', id: 'callType', key: 'callType' }}
                            >
                                {callTypeOptions.map(x =>
                                    <MenuItem key={x} value={x}>{this.getCallTypeText(x)}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Grid>

                    { /* Call Duration - dropdown */}
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth disabled={isReadOnly}>
                            <InputLabel htmlFor='callDurationMinutes'>Duration (mins)*</InputLabel>
                            <Select
                                value={this.state.editModel.callDurationMinutes || ''}
                                onChange={this.handleSelectChange}
                                inputProps={{ name: 'callDurationMinutes', id: 'callDurationMinutes', key: 'callDurationMinutes' }}
                            >
                                {callDurationOptions.map((x, i) =>
                                    <MenuItem key={`callDuration_${i}`} value={x}>{x}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Grid>

                    { /* CallLogDepartment - dropdown */}
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth disabled={isReadOnly}>
                            <InputLabel htmlFor='department'>Department *</InputLabel>
                            <Select
                                value={this.state.editModel.department || ''}
                                onChange={this.handleSelectChange}
                                inputProps={{ name: 'department', id: 'department', key: 'department' }}
                            >
                                {departments.map((x, i) =>
                                    <MenuItem key={x} value={x}>{this.getDepartmentText(x)}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Grid>

                    { /* Call with Owner - checkbox */}
                    <Grid item xs={12} sm={6} md={2}>
                        {!isReadOnly &&
                            <FormControlLabel disabled={isReadOnly}
                                label='Called Owner'
                                labelPlacement='end'
                                control={
                                    <Checkbox
                                        checked={this.state.editModel.isCallWithOwner}
                                        onChange={(e) => this.handleCheckboxChange(e, "isCallWithOwner")}
                                        value={"isCallWithOwner"}
                                        color='primary'
                                    />
                                }
                            />
                        }
                    </Grid>

                    {/* Internal notes - staff only - Label is caps per client request */}
                    {!isReadOnly &&
                        <Grid item xs={12}>
                        <TextField id='internalNotes' label='INTERNAL NOTES' multiline
                            defaultValue={this.state.editModel.internalNotes || ''} key={this.state.editModel.internalNotes || ''}
                            onBlur={this.handleTextBlur} onChange={this.handleTextChange}
                            fullWidth InputLabelProps={{ shrink: shrinkOverride }}
                            helperText={(editModel.id !== 0) ? notesInfoText() : ''}
                        />
                        </Grid>
                    }

                    {/* External notes - Label is caps per client request */}
                    <Grid item xs={12}>
                        <TextField id='externalNotes' label={(isReadOnly ? "Notes" : "CLIENT FACING NOTES")} multiline
                            defaultValue={this.state.editModel.externalNotes || ''} key={this.state.editModel.externalNotes || ''}
                            onBlur={this.handleTextBlur} onChange={this.handleTextChange} disabled={isReadOnly}
                            fullWidth InputLabelProps={{ shrink: shrinkOverride }}
                        />
                    </Grid>
                </Grid>
                <br />
            </div>
        );
    }

    renderContactNames = (rowData) => {
        let contactNames = "";
        let isFirst = true;
        rowData.contacts.forEach((contact) => {
            if (!isFirst) contactNames += ', ';
            contactNames += `${contact.contact.firstName} ${contact.contact.lastName}`;
            isFirst = false;
        });

        return <span>{contactNames}</span>;
    }

    renderCallDate = (rowData) => {
        let callDate = new Date(rowData.callDate);
        let d = String(callDate.getDate());
        let m = String(callDate.getMonth() + 1); //January is 0!
        let yyyy = callDate.getFullYear();

        return <span>{`${m}/${d}/${yyyy}`}</span>;
    }

    actionTemplate = (rowData, column) => {
        return (
            <div>
                <PrimeButton type="button" icon="pi pi-trash" className="p-button-danger"
                    disabled={rowData.usage > 0} title={rowData.usage > 0 ? `Used by ${rowData.usage} hospitals.` : ''}
                    onClick={(e) => this.onClickDelete(e, rowData)}
                />
            </div>
        );
    }

    renderCallLogTable = () => {
        if (this.state.isTableLoading)
            return <em>Loading...</em>;
        let key = 0;
        let columns = [
            <Column field="callDate" header="Call Date" sortable="true" body={this.renderCallDate} key={key++} />,
            <Column field="department" header="Department" body={(rowData) => <span>{this.getDepartmentText(rowData.department)}</span>} key={key++} />,
            <Column header="Contact(s)" body={this.renderContactNames} key={key++}/>
        ];

        // If read only, replace most columns with call notes.  Users don't need to render editor view.
        if (this.state.isReadOnly) {
            columns.push(<Column field="externalNotes" header="Notes" body={(rowData) => <span>{(rowData.externalNotes.length > 80 ? rowData.externalNotes.substring(0, 77) + '...' : rowData.externalNotes)}</span>} key={key++} />);
        }
        else {
            columns.push(<Column field="callDurationMinutes" header="Call Duration" style={(this.state.isReadOnly ? { display: "none" } : undefined)} key={key++} />);
            columns.push(<Column field="callType" header="Call Type" body={(rowData) => <span>{this.getCallTypeText(rowData.callType)}</span>} style={(this.state.isReadOnly ? { display: "none" } : undefined)} key={key++} />);
            columns.push(<Column field="isCallWithOwner" header="Call with Owner" body={(rowData) => <span>{(rowData.isCallWithOwner ? "Yes" : "No")}</span>} style={(this.state.isReadOnly ? { display: "none" } : undefined)} key={key++} />);
        }

        // only Staffmanagement and above users will see the Delete action column
        if (this.props.userRole === UserRoles.SuperUser || this.props.userRole === UserRoles.Administrator || this.props.userRole === UserRoles.StaffManagement) {
            columns.push(<Column body={this.actionTemplate} header={IsMobile ? "" : "Action"} style={{ textAlign: 'center', width: '6em' }} key={key++} />);
        }

        return (
            <div>
                <h3>Call History</h3>
                <DataTable responsive value={this.state.callLogEntries} onRowClick={this.onCallHistorySelect}
                    selectionMode="single" selection={this.getSelectedRow()}
                >
                    {columns.map(x => x)}
                </DataTable>
            </div>
        );
    }

    render() {
        return (
            <div>
                {!this.state.isReadOnly &&  // Editor only for staff.
                    <div>
                        {!this.state.isReadOnly &&  this.renderCallEditor()}
                        <br />
                        <hr />
                    </div>
                }

                {this.state.isReadOnly && this.state.editModel && this.state.editModel.id !== 0 &&
                    <div>
                        {this.renderCallViewer()}
                        <br />
                        <hr />
                    </div>
                }

                {this.renderCallLogTable()}

                <ModalOKCancel
                    title={this.state.modalTitle}
                    body={this.state.modalText}
                    show={this.state.showModal}
                    onOK={() => { if(this.state.modalAction) this.state.modalAction(); this.setState({ showModal: false }); }}
                    onCancel={() => this.setState({ showModal: false })}
                />
            </div>
        );
    }
}

export default withRouter(connect(state => state.login,
    dispatch => bindActionCreators(actionCreators, dispatch)
)(CallLog));


// WEBPACK FOOTER //
// ./src/components/Hospitals/CallLog.js